<template>
  <div class="min-h-screen bg-[#F6F4F0]">
    <GoogleTagManager />

    <GenderSelection
      v-if="currentStep === 'gender'"
      @gender-selected="handleGenderSelected"
    />

    <NameInput
      v-else-if="currentStep === 'name'"
      :gender="selectedGender"
      @name-submitted="handleNameSubmitted"
      @previous="currentStep = 'gender'"
    />

    <AgeSelection
      v-else-if="currentStep === 'age'"
      :gender="selectedGender"
      :first-name="firstName"
      @age-selected="handleAgeSelected"
      @previous="currentStep = 'name'"
    />

    <PeopleCircle
      v-else-if="currentStep === 'people'"
      @continue="handlePeopleCircleContinue"
      @previous="currentStep = 'age'"
    />

    <SalesQuiz
      v-else
      :full-name="fullName"
      @previous="currentStep = 'people'"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import SalesQuiz from './components/quiz/SalesQuiz.vue'
import GoogleTagManager from './utils/components/GoogleTagManager.vue'
import GenderSelection from './components/quiz/steps/GenderSelection/index.vue'
import NameInput from './components/quiz/steps/NameInput/index.vue'
import AgeSelection from './components/quiz/steps/AgeSelection/index.vue'
import PeopleCircle from './components/quiz/steps/PeopleCircle/index.vue'
import { sendQuizData, getUtmParams } from './utils'
import { initializeUtms, setupUtmWatcher } from './utils/services/utmManager'

type Step = 'gender' | 'name' | 'age' | 'people' | 'quiz'

const currentStep = ref<Step>('gender')
const selectedGender = ref<'male' | 'female'>('male')
const fullName = ref('')
const userAge = ref<number | null>(null)
const birthDate = ref<string>('')

const firstName = computed(() => fullName.value.split(' ')[0])

// Initialize state from URL
const initializeFromURL = () => {
  const params = new URLSearchParams(window.location.search)
  const step = params.get('step') as Step | null
  const gender = params.get('gender') as 'male' | 'female' | null
  const name = params.get('name')
  const quizStep = params.get('quizStep')

  if (step && gender) {
    selectedGender.value = gender
    if (step === 'name') {
      currentStep.value = 'name'
    } else if (step === 'age' && name) {
      fullName.value = decodeURIComponent(name)
      currentStep.value = 'age'
    } else if (step === 'people' && name) {
      fullName.value = decodeURIComponent(name)
      currentStep.value = 'people'
    } else if (step === 'quiz' && name) {
      fullName.value = decodeURIComponent(name)
      currentStep.value = 'quiz'

      // Store the quiz step in localStorage for SalesQuiz component
      if (quizStep) {
        localStorage.setItem('currentQuizStep', quizStep)
      }
    }
  }
}

// Watch for URL changes
watch(
  () => window.location.search,
  () => {
    initializeFromURL()
  }
)

// Event handlers
const handleGenderSelected = (gender: 'male' | 'female') => {
  selectedGender.value = gender
  currentStep.value = 'name'
}

const handleNameSubmitted = (name: string) => {
  fullName.value = name
  currentStep.value = 'age'
}

const handleAgeSelected = async (age: number, date: string) => {
  userAge.value = age
  birthDate.value = date
  currentStep.value = 'people'

  // Send data to webhook when user completes initial steps
  await sendQuizData({
    fullName: fullName.value,
    age: age,
    birthDate: date,
    step: currentStep.value,
    utms: getUtmParams()
  })

  // Update URL with current step
  const url = new URL(window.location.href)
  url.searchParams.set('quizStep', currentStep.value.toString())
  window.history.replaceState({}, '', url.toString())
}

const handlePeopleCircleContinue = () => {
  currentStep.value = 'quiz'
}

// Initialize on mount
onMounted(() => {
  initializeFromURL()
  initializeUtms()
  setupUtmWatcher()
})
</script>